import { lazy, type ComponentType } from 'react';

export interface PreloadablePage {
  Page: ComponentType;
  preload: () => Promise<void>;
}

/**
 * A lazily loaded page whose code can also be fetched before the first render. Once it is
 * loaded the page renders directly: React.lazy suspends on its first render even when the
 * code is already there, which would put the loading spinner over a prerendered snapshot
 * (scripts/prerender.mjs) for a moment before the same page came back.
 */
export function preloadablePage(load: () => Promise<{ default: ComponentType }>): PreloadablePage {
  let LoadedPage: ComponentType | null = null;
  const LazyPage = lazy(load);
  const preload = async (): Promise<void> => {
    LoadedPage = (await load()).default;
  };
  const Page = () => (LoadedPage ? <LoadedPage /> : <LazyPage />);
  return { Page, preload };
}
